import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useApi } from "@csa/web-foundation";
import type {
  ConfirmCancellationInput,
  EffectEndorsementResult,
  EndorsementListRow,
  EndorsementResult,
  PolicyDetail,
  RequestCancellationInput,
  RequestEndorsementInput,
} from "./types";

// ENDORSE-CANCEL-F — the policy-detail read + the endorsement/cancellation stage mutations
// over the shipped endpoints. State-affecting mutations invalidate the policy read so the
// surface re-renders the fresh status (the cancel flow is status-driven: ACTIVE → request →
// CANCELLATION_REQUESTED → confirm → CANCELLED). A CSA cancel is four-eyes server-side; the
// ApiClient's approval recovery drives it transparently (no bespoke handling here).
const policyKey = (id: string | undefined) => ["policy", id];
const endorsementsKey = (id: string | undefined) => ["policy", id, "endorsements"];

/** GET /v1/policies/:id — the flat policy detail (scope-filtered, policy:read). */
export function usePolicy(id: string | undefined) {
  const api = useApi();
  return useQuery({
    queryKey: policyKey(id),
    queryFn: () => api.get<PolicyDetail>(`/v1/policies/${id}`),
    enabled: !!id,
  });
}

/** A2 — GET …/endorsements: the persisted list (BARE ARRAY), newest first. */
export function useEndorsements(id: string | undefined) {
  const api = useApi();
  return useQuery({
    queryKey: endorsementsKey(id),
    queryFn: () => api.get<EndorsementListRow[]>(`/v1/policies/${id}/endorsements`),
    enabled: !!id,
  });
}

/** POST …/endorsements — request a NON_PREMIUM_AFFECTING endorsement (application:write). */
export function useRequestEndorsement(id: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: RequestEndorsementInput) =>
      api.post<EndorsementResult>(`/v1/policies/${id}/endorsements`, input),
    // A2 — the list is now persisted, so a new request must show up in it.
    onSettled: () => void qc.invalidateQueries({ queryKey: endorsementsKey(id) }),
  });
}

/** POST …/endorsements/:eid/effect — effect a non-premium endorsement (application:issue). */
export function useEffectEndorsement(id: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (endorsementId: string) =>
      api.post<EffectEndorsementResult>(`/v1/policies/${id}/endorsements/${endorsementId}/effect`),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: policyKey(id) });
      void qc.invalidateQueries({ queryKey: endorsementsKey(id) });
    },
  });
}

/** POST …/cancel — request cancellation (policy:cancel; CSA subtype is four-eyes). */
export function useRequestCancel(id: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: RequestCancellationInput) =>
      api.post<PolicyDetail>(`/v1/policies/${id}/cancel`, input),
    onSettled: () => void qc.invalidateQueries({ queryKey: policyKey(id) }),
  });
}

/** POST …/cancel/confirm — confirm cancellation → CANCELLED (policy:cancel). */
export function useConfirmCancel(id: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ConfirmCancellationInput) =>
      api.post<PolicyDetail>(`/v1/policies/${id}/cancel/confirm`, input),
    onSettled: () => void qc.invalidateQueries({ queryKey: policyKey(id) }),
  });
}
